import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Box, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import OpportunityListSkipper from './OpportunityListSkipper';
import MatchListCrew from './MatchListCrew';
import PublicRoster from './PublicRoster';
import { isProfileEmpty } from '../api/api';

const Body = ({ profile, onProfileUpdated, domain }) => {
  const { t } = useTranslation();
  const { user, getToken } = useKindeAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState(null);
  const [skipperView, setSkipperView] = useState('roster'); // 'roster' or 'events'
  const [crewView, setCrewView] = useState('matches'); // 'matches' or 'roster'

  // Check if profile is completed (all steps done)
  const isProfileCompleted = profile?.completedSteps && 
    Object.values(profile.completedSteps).filter(v => v === true).length >= 3;

  useEffect(() => {
    if (isProfileEmpty(profile) && location.pathname !== '/profile') {
      navigate('/profile');
    }
    const fetchToken = async () => {
      const authToken = await getToken();
      setToken(authToken);
    };
    fetchToken();
  }, [profile, location, navigate, getToken]);

  // Default to roster for skippers with completed profile
  useEffect(() => {
    if (profile?.role === 'skipper' && isProfileCompleted) {
      setSkipperView('roster');
    }
  }, [profile, isProfileCompleted]);

  const handleProfileUpdate = () => {
    onProfileUpdated();
    navigate('/');
  };

  const handleSkipperViewChange = (event, newValue) => {
    setSkipperView(newValue);
  };

  const handleCrewViewChange = (event, newValue) => {
    setCrewView(newValue);
  };

  const handleNavigateToOpportunities = () => {
    navigate('/opportunity');
  };

  return (
    <Box 
      className="App-content"
      sx={{ 
        flex: 1, 
        overflow: 'auto',
        position: 'relative',
        height: '100%',
        WebkitOverflowScrolling: 'touch', /* Improve scroll on iOS */
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/logo1024bw.png")',
          backgroundSize: '50%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.1,
          pointerEvents: 'none',
          zIndex: 0
        }
      }}
    >
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="flex-start" 
        width="100%"
      >
        {profile.role === 'skipper' && token && (
          <>
            <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={skipperView} 
                onChange={handleSkipperViewChange}
                centered
              >
                <Tab 
                  icon={<PeopleIcon />} 
                  label={t('skipper.tabs.roster')} 
                  value="roster"
                  iconPosition="start"
                />
                <Tab 
                  icon={<EventIcon />} 
                  label={t('skipper.tabs.events')} 
                  value="events"
                  iconPosition="start"
                />
              </Tabs>
            </Box>
            <Box width="100%">
              {skipperView === 'roster' ? (
                <PublicRoster
                  token={token}
                  domain={domain}
                  currentUserId={user?.id}
                  onNavigateToOpportunities={handleNavigateToOpportunities}
                />
              ) : (
                <OpportunityListSkipper token={token} domain={domain} profile={profile} />
              )}
            </Box>
          </>
        )}
        {profile.role === 'crew' && token && (
          <>
            <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={crewView}
                onChange={handleCrewViewChange}
                centered
              >
                <Tab
                  icon={<EventIcon />}
                  label={t('crew.tabs.matches')}
                  value="matches"
                  iconPosition="start"
                />
                <Tab
                  icon={<PeopleIcon />}
                  label={t('crew.tabs.roster')}
                  value="roster"
                  iconPosition="start"
                />
              </Tabs>
            </Box>
            <Box width="100%">
              {crewView === 'matches' ? (
                <MatchListCrew token={token} domain={domain} homeclub={profile.homeClub} showInPublicRoster={profile.showInPublicRoster} />
              ) : (
                <PublicRoster token={token} domain={domain} currentUserId={user?.id} canInvite={false} />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Body;
